import React, { useEffect, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { getSocket } from "../../../../../api/socket";
import backend from "../../../../../api";
import "./IncomingLocationListener.css";

/**
 * Shows a modal when someone requests your location,
 * asks permission, and (if accepted) shares once + starts a short live-share.
 */
export default function IncomingLocationListener() {
  const { user } = useSelector((s) => s.auth);
  const userId = user?._id;

  const [showPopup, setShowPopup] = useState(false);
  const [requesterName, setRequesterName] = useState("Someone");
  const [pendingReq, setPendingReq] = useState(null);
  const ringRef = useRef(null);
  const unlockedRef = useRef(false);
  const watchRef = useRef(null);
  const endTimerRef = useRef(null);

  // unlock autoplay on first interaction
  useEffect(() => {
    const unlock = () => {
      if (unlockedRef.current) return;
      const el = ringRef.current;
      if (!el) return;
      el.muted = true;
      Promise.resolve(el.play())
        .then(() => {
          el.pause(); el.currentTime = 0; el.muted = false;
          unlockedRef.current = true;
          document.removeEventListener("click", unlock);
          document.removeEventListener("keydown", unlock);
        })
        .catch(() => {});
    };
    document.addEventListener("click", unlock);
    document.addEventListener("keydown", unlock);
    return () => {
      document.removeEventListener("click", unlock);
      document.removeEventListener("keydown", unlock);
    };
  }, []);

  const finish = () => {
    try { ringRef.current?.pause(); } catch {}
    if (ringRef.current) ringRef.current.currentTime = 0;
    setShowPopup(false);
    setPendingReq(null);
  };

  const stopLiveShare = () => {
    if (watchRef.current) {
      try { navigator.geolocation.clearWatch(watchRef.current); } catch {}
      watchRef.current = null;
    }
    if (endTimerRef.current) {
      clearTimeout(endTimerRef.current);
      endTimerRef.current = null;
    }
  };

  useEffect(() => {
    const socket = getSocket();
    if (!socket || !userId) return;

    const onReq = async (data) => {
      // server emits: { to, from, fromName }
      if (!data || data.to && data.to !== userId) return;
      setRequesterName(data.fromName || "Someone");
      setPendingReq({ from: data.from, fromName: data.fromName });
      setShowPopup(true);
      ringRef.current?.play().catch(() => {});
    };

    socket.on("location:request", onReq);

    return () => {
      socket.off("location:request", onReq);
      stopLiveShare();
    };
  }, [userId]);

  const accept = async () => {
    try { ringRef.current?.pause(); } catch {}
    if (ringRef.current) ringRef.current.currentTime = 0;
    setShowPopup(false);

    // One-shot update (saves + broadcasts)
    navigator.geolocation.getCurrentPosition(
      async (pos) => {
        const { latitude: lat, longitude: lng, accuracy } = pos.coords;
        try {
          await backend.post("/location", { lat, lng, accuracy }, { params: { userId } });
        } catch {}
      },
      () => {},
      { enableHighAccuracy: true }
    );

    // Short live share (e.g. 5 minutes) — can tweak duration
    stopLiveShare();
    watchRef.current = navigator.geolocation.watchPosition(
      async (pos) => {
        const { latitude: lat, longitude: lng, accuracy } = pos.coords;
        try {
          await backend.post("/location", { lat, lng, accuracy }, { params: { userId } });
        } catch {}
      },
      () => {},
      { enableHighAccuracy: true, maximumAge: 0, timeout: 15000 }
    );
    endTimerRef.current = setTimeout(stopLiveShare, 5 * 60 * 1000);
  };

  const decline = () => {
    try { ringRef.current?.pause(); } catch {}
    if (ringRef.current) ringRef.current.currentTime = 0;
    setShowPopup(false);
  };

  return (
    <>
      <audio ref={ringRef} loop preload="auto"
        src="https://freesound.org/data/previews/66/66176_634166-lq.mp3" />
      {showPopup && (
        <div className="locl-modal" role="dialog" aria-modal="true">
          <div className="locl-modal-content">
            <h3>Location request</h3>
            <p>{requesterName} wants to see your location.</p>
            <div className="locl-actions">
              <button className="btn-brand" onClick={accept}>Share</button>
              <button className="btn-light" onClick={decline}>Ignore</button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
